import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Unlock, ArrowLeft, ArrowRight, ShoppingBag, FileCheck, Search, BarChart3,
  AlertTriangle, Shield, Play, CheckCircle, Clock, FileText, Zap, Ban,
  DollarSign, Lock, ChevronDown, Star, Award, Video, BookOpen, ChevronRight, Globe
} from 'lucide-react';
import { usePrices } from '../context/PricesContext';

// Products config (prices loaded from context)
const productsConfig = [
  {
    id: 'critical-mistakes',
    priceKey: 'critical_mistakes',
    name: 'Критические ошибки',
    shortDesc: 'Опыт, который обычно обходится в тысячи долларов',
    icon: AlertTriangle,
    color: 'rose',
    features: ['30 мин видео', 'Чек-лист', 'Доступ навсегда'],
    featured: true,
    badge: 'МИНИ-КУРС',
    hasTimer: true,
    hasOldPrice: true,
  },
  {
    id: 'ungating',
    priceKey: 'ungating',
    name: 'Ангейтинг',
    shortDesc: 'Открытие закрытых брендов на Amazon',
    icon: Unlock,
    color: 'amber',
    features: ['3 видео-урока', '1 личный созвон'],
  },
  {
    id: 'supplier-invoice',
    priceKey: 'supplier_invoice',
    name: 'Поставщик и инвойс',
    shortDesc: 'Как Amazon проверяет поставщика и инвойс',
    icon: FileCheck,
    color: 'blue',
    features: ['2 видео-урока', 'Критерии проверок'],
  },
  {
    id: 'sourcing-pack',
    priceKey: 'sourcing_pack',
    name: 'Sourcing Pack',
    shortDesc: 'Поиск товаров и поставщиков — системно',
    icon: Search,
    color: 'emerald',
    features: ['22 видео', '4 практики', '3 созвона'],
  },
  {
    id: 'asin-checker',
    priceKey: 'asin_checker',
    name: 'ASIN Checker',
    shortDesc: 'Массовая проверка открытых/закрытых товаров',
    icon: Search,
    color: 'cyan',
    features: ['Софт', 'Excel-отчёты'],
  },
  {
    id: 'repricer',
    priceKey: 'repricer',
    name: 'Репрайсер',
    shortDesc: 'Настройка под прибыль, а не «авто-режим»',
    icon: BarChart3,
    color: 'violet',
    features: ['1 видео', '1 созвон'],
  },
  {
    id: 'website',
    priceKey: 'website',
    name: 'Разработка сайта',
    shortDesc: 'Профессиональный сайт для работы с поставщиками',
    isTextPrice: true,
    icon: Globe,
    color: 'indigo',
    features: ['Домен бесплатно', 'Почта на домене', 'Премиум дизайн'],
    badge: 'NEW',
  },
];

const colorStyles = {
  amber: { bg: 'from-amber-500/10 to-amber-900/20', border: 'border-amber-500/30', hoverBorder: 'hover:border-amber-500/60', icon: 'from-amber-500 to-orange-600', text: 'text-amber-400', button: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-500/20', glow: 'bg-amber-500/30' },
  blue: { bg: 'from-blue-500/10 to-blue-900/20', border: 'border-blue-500/30', hoverBorder: 'hover:border-blue-500/60', icon: 'from-blue-500 to-indigo-600', text: 'text-blue-400', button: 'from-blue-500 to-indigo-500', shadow: 'shadow-blue-500/20', glow: 'bg-blue-500/30' },
  violet: { bg: 'from-violet-500/10 to-violet-900/20', border: 'border-violet-500/30', hoverBorder: 'hover:border-violet-500/60', icon: 'from-violet-500 to-purple-600', text: 'text-violet-400', button: 'from-violet-500 to-purple-500', shadow: 'shadow-violet-500/20', glow: 'bg-violet-500/30' },
  emerald: { bg: 'from-emerald-500/10 to-emerald-900/20', border: 'border-emerald-500/30', hoverBorder: 'hover:border-emerald-500/60', icon: 'from-emerald-500 to-teal-600', text: 'text-emerald-400', button: 'from-emerald-500 to-teal-500', shadow: 'shadow-emerald-500/20', glow: 'bg-emerald-500/30' },
  cyan: { bg: 'from-cyan-500/10 to-cyan-900/20', border: 'border-cyan-500/30', hoverBorder: 'hover:border-cyan-500/60', icon: 'from-cyan-500 to-blue-600', text: 'text-cyan-400', button: 'from-cyan-500 to-blue-500', shadow: 'shadow-cyan-500/20', glow: 'bg-cyan-500/30' },
  rose: { bg: 'from-rose-500/10 to-rose-900/20', border: 'border-rose-500/30', hoverBorder: 'hover:border-rose-500/60', icon: 'from-rose-500 to-red-600', text: 'text-rose-400', button: 'from-rose-500 to-amber-500', shadow: 'shadow-rose-500/20', glow: 'bg-rose-500/30' },
  indigo: { bg: 'from-indigo-500/10 to-indigo-900/20', border: 'border-indigo-500/30', hoverBorder: 'hover:border-indigo-500/60', icon: 'from-indigo-500 to-violet-600', text: 'text-indigo-400', button: 'from-indigo-500 to-violet-500', shadow: 'shadow-indigo-500/20', glow: 'bg-indigo-500/30' },
};

// Discount Timer Component - Static 7-day cycle (global for all users)
const DiscountTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    // Calculate time until next Sunday midnight UTC (7-day cycle)
    const getTimeUntilNextCycle = () => {
      const now = new Date();
      // Find next Sunday (or current if Sunday)
      const daysUntilSunday = (7 - now.getUTCDay()) % 7 || 7;
      const nextSunday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + daysUntilSunday,
        0, 0, 0, 0
      ));
      return nextSunday.getTime() - now.getTime();
    };
    
    const updateTimer = () => {
      const diff = getTimeUntilNextCycle();
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);
  
  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-zinc-900 border border-rose-500/30 flex items-center justify-center">
        <span className="text-lg sm:text-xl font-black text-white">{String(value).padStart(2, '0')}</span>
      </div>
      <span className="text-[10px] sm:text-xs text-zinc-500 mt-1">{label}</span>
    </div>
  );
  
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-rose-500/10 via-amber-500/10 to-rose-500/10 border border-rose-500/30">
      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400 flex-shrink-0" />
      <span className="text-xs sm:text-sm text-zinc-300 font-medium">Скидка истекает:</span>
      <div className="flex items-center gap-1 sm:gap-2">
        <TimeBlock value={timeLeft.days} label="дн" />
        <span className="text-rose-400 font-bold text-lg">:</span>
        <TimeBlock value={timeLeft.hours} label="ч" />
        <span className="text-rose-400 font-bold text-lg">:</span>
        <TimeBlock value={timeLeft.minutes} label="мин" />
        <span className="text-rose-400 font-bold text-lg hidden sm:block">:</span>
        <div className="hidden sm:block">
          <TimeBlock value={timeLeft.seconds} label="сек" />
        </div>
      </div>
    </div>
  );
};

// Mini-Course Expandable Card - ENHANCED VERSION
const MiniCourseCard = ({ product, isExpanded, onToggle }) => {
  const colors = colorStyles[product.color];
  const Icon = product.icon;
  
  return (
    <div id="critical-mistakes" className="scroll-mt-24">
      <div className="relative group">
        {/* Animated glow for featured */}
        <div className={`absolute -inset-3 ${colors.glow} rounded-3xl blur-3xl opacity-60 group-hover:opacity-90 transition-opacity duration-500 animate-pulse`} />
        
        <div className={`relative rounded-3xl overflow-hidden border-2 ${colors.border} bg-gradient-to-b ${colors.bg} transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl ${colors.shadow}`}>
          
          {/* Top accent - thicker */}
          <div className="h-2 bg-gradient-to-r from-rose-500 via-amber-500 to-rose-500 animate-gradient bg-[length:200%_auto]" />
          
          <div className="p-6 sm:p-8">
            {/* Header row - BIGGER */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 mb-6">
              <div className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br ${colors.icon} flex items-center justify-center shadow-xl flex-shrink-0`}>
                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                {/* Ping effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colors.icon} animate-ping opacity-20`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-sm font-bold">
                    {product.badge}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-sm font-bold">
                    НОВИНКА
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-2">
                  {product.name}
                </h3>
                <p className="text-zinc-400 text-base sm:text-lg lg:text-xl">
                  {product.shortDesc}
                </p>
              </div>
              
              {/* Price - BIGGER */}
              <div className="text-left sm:text-right flex-shrink-0">
                {product.oldPrice && <p className="text-zinc-500 text-base line-through">${product.oldPrice}</p>}
                <p className={`text-4xl sm:text-5xl font-black ${colors.text}`}>
                  {product.priceText || `$${product.price}`}
                </p>
              </div>
            </div>
            
            {/* Features - BIGGER */}
            <div className="flex flex-wrap gap-3 mb-6">
              {product.features.map((feature, i) => (
                <span key={i} className="px-4 py-2 rounded-full bg-zinc-800/50 text-zinc-200 text-base font-semibold border border-zinc-700/50">
                  {feature}
                </span>
              ))}
            </div>
            
            {/* Discount Timer */}
            {product.hasTimer && (
              <div className="mb-6">
                <DiscountTimer />
              </div>
            )}
            
            {/* Expand/Collapse button - BIGGER */}
            <button
              onClick={onToggle}
              className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-zinc-600 transition-all text-zinc-200 font-bold text-lg mb-6"
              style={{ touchAction: 'manipulation' }}
            >
              <span>{isExpanded ? 'Скрыть детали' : 'Подробнее о курсе'}</span>
              <ChevronDown className={`w-6 h-6 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Expandable content - RESTRUCTURED */}
            <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              
              {/* SECTION 1: Story */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-rose-400" />
                  </div>
                  Почему этот курс появился
                </h4>
                <div className="p-5 sm:p-6 rounded-2xl bg-zinc-800/40 border border-zinc-700/50 space-y-4">
                  <p className="text-zinc-300 text-base sm:text-lg">
                    В 2023 году я по ошибке отправил на Amazon партию товара,
                    которая должна была уйти в брак.
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-white">
                    Результат — аккаунт был заблокирован на <span className="text-rose-400">4 месяца</span>.
                  </p>
                  <p className="text-xl sm:text-2xl font-black text-amber-400">
                    Это была одна ошибка.
                  </p>
                </div>
              </div>
              
              {/* SECTION 2: What I did */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Search className="w-5 h-5 text-blue-400" />
                  </div>
                  Что пришлось сделать
                </h4>
                <div className="space-y-3">
                  {[
                    { icon: DollarSign, text: 'Потратить более $600 на внутренние материалы и кейсы', color: 'amber' },
                    { icon: Search, text: 'Разобрать десятки реальных ситуаций', color: 'blue' },
                    { icon: Shield, text: 'Изучить опыт коллег и учеников, которые допускали те же ошибки', color: 'emerald' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/30">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorStyles[item.color].icon} flex items-center justify-center flex-shrink-0`}>
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-zinc-200 text-base sm:text-lg font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* SECTION 3: What it is - ВАЖНО block */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-400" />
                  </div>
                  Что это за продукт
                </h4>
                <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30">
                  <p className="text-zinc-300 text-base sm:text-lg mb-4">
                    Это <span className="text-white font-bold">не теория</span> и не «как правильно по правилам».
                  </p>
                  
                  {/* ВАЖНО block - BIGGER */}
                  <div className="relative p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-rose-500/20 via-amber-500/15 to-rose-500/20 border-2 border-rose-500/40 mb-6">
                    <div className="absolute -top-3 left-4 px-3 py-1 rounded bg-rose-500 text-white text-xs font-black uppercase tracking-wider">
                      Важно
                    </div>
                    <p className="text-white font-black text-xl sm:text-2xl lg:text-3xl pt-2">
                      Это список <span className="text-rose-400">ВСЕХ</span> ошибок, которые я когда-либо видел или допускал сам
                    </p>
                  </div>
                  
                  <h5 className="text-white font-bold text-lg sm:text-xl mb-4">
                    Ошибки, которые:
                  </h5>
                  <div className="space-y-3 mb-4">
                    {[
                      { icon: Ban, text: 'Приводят к блокировкам аккаунтов', color: 'rose' },
                      { icon: AlertTriangle, text: 'Заканчиваются утилизацией товара', color: 'amber' },
                      { icon: DollarSign, text: 'Стоят денег и месяцев простоя', color: 'violet' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/50">
                        <item.icon className={`w-6 h-6 ${colorStyles[item.color].text} flex-shrink-0`} />
                        <span className={`font-semibold text-base sm:text-lg ${colorStyles[item.color].text}`}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-emerald-400 font-bold text-lg sm:text-xl">
                    ✓ И правильные действия, которые нужно делать с первого раза.
                  </p>
                </div>
              </div>
              
              {/* SECTION 4: Who is it for */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-violet-400" />
                  </div>
                  Кому подойдёт
                </h4>
                <div className="p-5 sm:p-6 rounded-2xl bg-zinc-800/30 border border-zinc-700/30">
                  <p className="text-zinc-400 text-base sm:text-lg mb-4">Не важно, на каком вы этапе:</p>
                  <div className="grid sm:grid-cols-2 gap-3 mb-4">
                    {[
                      'Только рассматриваете Amazon',
                      'Планируете регистрировать аккаунт',
                      'Уже продаёте и работаете с поставщиками',
                      'Сталкивались с отказами или жалобами',
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span className="text-zinc-200 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-amber-400 font-bold text-base sm:text-lg">
                    Мини-курс помогает избежать типичных ошибок, которые совершают почти все новички.
                  </p>
                </div>
              </div>
              
              {/* SECTION 5: What's included */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Play className="w-5 h-5 text-emerald-400" />
                  </div>
                  Что входит
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-5 sm:p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30">
                    <div className="flex items-center gap-3 mb-3">
                      <Video className="w-8 h-8 text-blue-400" />
                      <span className="text-white font-black text-xl">Видео</span>
                    </div>
                    <p className="text-blue-400 font-bold text-2xl sm:text-3xl mb-3">30 минут</p>
                    <ul className="space-y-2 text-zinc-300">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">•</span>
                        <span>Какие ошибки были допущены</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">•</span>
                        <span>К чему они приводят</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">•</span>
                        <span>Как нужно было сделать правильно</span>
                      </li>
                    </ul>
                  </div>
                  <div className="p-5 sm:p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="w-8 h-8 text-emerald-400" />
                      <span className="text-white font-black text-xl">Чек-лист</span>
                    </div>
                    <p className="text-emerald-400 font-bold text-2xl sm:text-3xl mb-3">Подробный</p>
                    <ul className="space-y-2 text-zinc-300">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">•</span>
                        <span>Ошибка</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">•</span>
                        <span>Как было сделано</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">•</span>
                        <span>Как нужно делать</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">•</span>
                        <span>На что обращать внимание</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* SECTION 6: Why it matters */}
              <div className="mb-8">
                <h4 className="text-xl sm:text-2xl font-black text-white mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-rose-400" />
                  </div>
                  Почему это важно
                </h4>
                <div className="p-5 sm:p-6 rounded-2xl bg-rose-500/10 border border-rose-500/30">
                  <p className="text-zinc-300 text-base sm:text-lg mb-4">Одна ошибка может:</p>
                  <div className="space-y-3 mb-4">
                    {[
                      { icon: Lock, text: 'Сделать аккаунт неактивным' },
                      { icon: AlertTriangle, text: 'Отправить партию товара в утиль' },
                      { icon: DollarSign, text: 'Лишить вас денег из-за проблемного поставщика' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-rose-500/10">
                        <item.icon className="w-6 h-6 text-rose-400 flex-shrink-0" />
                        <span className="text-rose-200 text-base sm:text-lg font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-white font-black text-lg sm:text-xl">
                    Стоимость таких ошибок всегда выше цены этого курса.
                  </p>
                </div>
              </div>
              
              {/* SECTION 7: Final message */}
              <div className="text-center p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/30 mb-6">
                <p className="text-zinc-300 text-base sm:text-lg mb-3">
                  Вы получаете чужой опыт, который обычно приобретается через
                </p>
                <p className="text-rose-400 font-black text-xl sm:text-2xl mb-4">
                  потери и блокировки
                </p>
                <p className="text-white font-black text-lg sm:text-xl">
                  Этот мини-курс нужен, чтобы вам не пришлось проходить этот путь самому.
                </p>
              </div>
            </div>
            
            {/* CTA - BIGGER */}
            <Link
              to={`/catalog/${product.id}`}
              className={`group/btn relative flex items-center justify-center gap-3 w-full px-8 py-5 bg-gradient-to-r ${colors.button} text-white font-black text-xl rounded-2xl shadow-lg ${colors.shadow} hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 overflow-hidden`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
              <span className="relative">
                {product.priceText ? product.priceText : `Получить за $${product.price}`}
              </span>
              <ArrowRight className="relative w-6 h-6 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Regular Product Card
const ProductCard = ({ product }) => {
  const colors = colorStyles[product.color];
  const Icon = product.icon;
  
  return (
    <Link 
      to={`/catalog/${product.id}`}
      id={product.id}
      className={`group relative block p-5 sm:p-6 rounded-2xl bg-gradient-to-b ${colors.bg} border-2 ${colors.border} ${colors.hoverBorder} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${colors.shadow} scroll-mt-24`}
    >
      {/* Mobile: Stack layout */}
      <div className="sm:hidden">
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.icon} flex items-center justify-center shadow-lg flex-shrink-0`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-white mb-1 group-hover:text-amber-400 transition-colors">
              {product.name}
            </h3>
            <p className="text-zinc-400 text-sm line-clamp-2">
              {product.shortDesc}
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-3">
          {product.features.map((feature, i) => (
            <span key={i} className="px-2 py-1 rounded-full bg-zinc-800/50 text-zinc-300 text-xs">
              {feature}
            </span>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-zinc-800/50">
          <p className={`text-xl font-black ${colors.text}`}>
            {product.priceText || `$${product.price}`}
          </p>
          <div className="flex items-center gap-1 text-zinc-500 text-sm">
            <span>Подробнее</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
      
      {/* Desktop: Side by side layout */}
      <div className="hidden sm:flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.icon} flex items-center justify-center shadow-lg flex-shrink-0`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-black text-white mb-1 group-hover:text-amber-400 transition-colors">
            {product.name}
          </h3>
          <p className="text-zinc-400 text-sm mb-3 line-clamp-2">
            {product.shortDesc}
          </p>
          
          <div className="flex flex-wrap gap-1.5 mb-3">
            {product.features.map((feature, i) => (
              <span key={i} className="px-2 py-1 rounded-full bg-zinc-800/50 text-zinc-300 text-xs">
                {feature}
              </span>
            ))}
          </div>
        </div>
        
        <div className="text-right flex-shrink-0">
          <p className={`text-2xl font-black ${colors.text}`}>
            {product.priceText || `$${product.price}`}
          </p>
          <div className="flex items-center gap-1 text-zinc-500 text-sm mt-1">
            <span>Подробнее</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Navigation Tabs
const NavTabs = ({ activeTab, setActiveTab }) => {
  const tabs = [
    { id: 'all', label: 'Все', icon: ShoppingBag },
    { id: 'critical-mistakes', label: 'Мини-курс', icon: AlertTriangle, badge: 'NEW' },
    { id: 'ungating', label: 'Ангейтинг', icon: Unlock },
    { id: 'sourcing-pack', label: 'Sourcing', icon: Search },
    { id: 'other', label: 'Другие', icon: BarChart3 },
  ];

  const scrollTo = (id) => {
    setActiveTab(id);
    if (id === 'all') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (id === 'other') {
      document.getElementById('supplier-invoice')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="sticky top-16 z-40 -mx-4 px-4 py-3 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => scrollTo(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-all duration-300 ${
                isActive 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-lg shadow-amber-500/30' 
                  : 'bg-zinc-800/80 text-zinc-300 hover:bg-zinc-700 hover:text-white'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isActive ? 'bg-black/20 text-black' : 'bg-rose-500/20 text-rose-400'}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CatalogPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [miniCourseExpanded, setMiniCourseExpanded] = useState(false);
  
  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove #
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Highlight effect
          element.classList.add('ring-2', 'ring-amber-500', 'ring-offset-2', 'ring-offset-black');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-amber-500', 'ring-offset-2', 'ring-offset-black');
          }, 2000);
        }
      }, 500);
    }
  }, []);
  
  // Use prices from context
  const { prices, getPrice, getOldPrice, getPriceText } = usePrices();
  
  // Map products with prices from context
  const products = productsConfig.map(p => ({
    ...p,
    price: p.isTextPrice ? null : getPrice(p.priceKey),
    oldPrice: p.hasOldPrice ? getOldPrice(p.priceKey) : null,
    priceText: p.isTextPrice ? getPriceText(p.priceKey) : null,
  }));
  
  const miniCourse = products.find(p => p.featured);
  const regularProducts = products.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-[#030304]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg blur opacity-40 group-hover:opacity-70 transition-opacity" />
                <div className="relative w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <span className="text-black font-black">A</span>
                </div>
              </div>
              <span className="text-white font-bold hidden sm:inline">Amazon Wholesale</span>
            </Link>
            
            <Link 
              to="/"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">На главную</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Page Header */}
          <div className="text-center py-8 sm:py-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <ShoppingBag className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 font-semibold text-sm">Каталог продуктов</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3">
              <span className="text-white">Дополнительные </span>
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">продукты</span>
            </h1>
            
            <p className="text-zinc-400 mb-4 text-sm sm:text-base">
              Мини-курсы для решения конкретных задач
            </p>
            
            {/* Notice */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500/10 border border-violet-500/30">
              <Award className="w-4 h-4 text-violet-400" />
              <span className="text-violet-300 text-sm">
                Для <span className="font-bold text-violet-400">опытных продавцов</span>
              </span>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <NavTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          
          {/* Products */}
          <div className="py-6 space-y-4">
            {/* Mini-Course - Featured */}
            {miniCourse && (
              <MiniCourseCard 
                product={miniCourse} 
                isExpanded={miniCourseExpanded}
                onToggle={() => setMiniCourseExpanded(!miniCourseExpanded)}
              />
            )}
            
            {/* Regular Products */}
            {regularProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Back to main */}
          <div className="text-center py-8">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">На главную</span>
            </Link>
          </div>
        </div>
      </main>
      
      {/* Hide scrollbar for tabs */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default CatalogPage;
